<script lang="ts" module>
	import { writable } from 'svelte/store';
	export const showGuestListEdit = writable(false);
</script>

<script lang="ts">
	import InputText, { inputColorClasses } from '$lib/InputText.svelte';
	import { currentEvent } from '$lib/currentEvent';
	import IconTrash from '$lib/icons/IconTrash.svelte';
	import { debounce } from 'lodash-es';
	import { addGuest, moveUpWaitlistEntry, removeGuest, updateGuest } from './page.telefunc';
	import { invalidateAll } from '$app/navigation';
	import type { Guest } from '$lib/server/supabase';
	import { flip } from 'svelte/animate';
	import { cubicInOut } from 'svelte/easing';
	import { fade } from 'svelte/transition';
	import Modal from '$lib/Modal.svelte';
	import IconPlusCircle from '$lib/icons/IconPlusCircle.svelte';
	import { Attending } from '$lib/eventTypes';
	import LoadingButton from '$lib/LoadingButton.svelte';
	import { isMaybeAnswerEnabled } from '$lib/commonLogic';
	import viewport from '$lib/actions/viewport';
	import * as m from '$lib/paraglide/messages.js';

	let newGuestName = $state('');
	let newGuestAttending = $state('');

	let guests = $derived(
		$currentEvent.guests
			.filter((x) => x.attending !== Attending.WAITLIST)
			.sort((a, b) => new Date(a.createdAt ?? '').getTime() - new Date(b.createdAt ?? '').getTime())
			.sort((a, b) => a.attending.localeCompare(b.attending))
	);

	let waitlist = $derived($currentEvent.guests.filter((x) => x.attending === Attending.WAITLIST));

	async function onRemoveGuest(guest: Guest) {
		let message = m.guestList_deleteGuestConfirm({ name: guest.name });
		if (
			$currentEvent.maxGuests &&
			guests.length >= $currentEvent.maxGuests &&
			guest.attending === Attending.ATTENDING &&
			waitlist.length > 0
		)
			message = m.guestList_deleteGuestWithWaitlistInfo({ name: guest.name });

		if (!confirm(message)) {
			return;
		}
		await removeGuest($currentEvent.ownerSecret, guest.userId);
		await invalidateAll();
	}

	async function onAttendingChanged(guest: Guest) {
		if (
			$currentEvent.maxGuests &&
			guests.length >= $currentEvent.maxGuests &&
			guest.attending === Attending.NOT_ATTENDING &&
			waitlist.length > 0 &&
			!confirm(m.guestList_declineGuestWithWaitlistInfo({ name: guest.name }))
		) {
			await invalidateAll();
			return;
		}

		await updateGuest($currentEvent.ownerSecret!, {
			userId: guest.userId,
			attending: guest.attending,
			name: guest.name,
		});
		invalidateAll();
	}

	const guestNameChanged = debounce(async (guest: Guest) => {
		await updateGuest($currentEvent.ownerSecret!, {
			userId: guest.userId,
			name: guest.name,
			attending: guest.attending,
		});
		invalidateAll();
	}, 700);

	async function addNewGuest() {
		const cleanName = newGuestName?.trim();
		if (!cleanName) {
			alert(m.guestList_enterGuestName());
			return;
		}
		if (!newGuestAttending) {
			alert(m.guestList_selectGuestAttendance());
			return;
		}

		await addGuest($currentEvent.ownerSecret!, { name: newGuestName, attending: newGuestAttending as Attending });
		await invalidateAll();
		newGuestAttending = '';
		newGuestName = '';
	}

	async function onMoveUpWaitlistEntry(guest: Guest) {
		await moveUpWaitlistEntry($currentEvent.ownerSecret!, guest.userId);
		await invalidateAll();
	}

	let waitlistInView = $state(false);
	let waitlistTitleElement: HTMLElement | undefined = $state();
</script>

{#if $showGuestListEdit}
	<Modal
		title={$currentEvent.isPublic ? m.guestList_editParticipants() : m.guestList_editGuests()}
		onclose={() => ($showGuestListEdit = false)}
		paddingAndMarginMobile={false}
	>
		<div class="px-2 py-4 sm:p-0">
			{#if $currentEvent.maxGuests && !waitlistInView}
				<button class="link mb-4" onclick={() => waitlistTitleElement?.scrollIntoView({ behavior: 'smooth' })}>
					{m.guestList_toWaitlist()}
				</button>
			{/if}

			<ul class="md:mr-4">
				<div class="mb-4 flex">
					<InputText
						bind:value={newGuestName}
						name="name"
						placeholder={m.guestList_newGuest()}
						autocomplete="off"
						class="mr-2 w-full sm:mr-4"
					/>
					<select bind:value={newGuestAttending} class={inputColorClasses}>
						<option value="" disabled selected>{m.guestList_select()}</option>
						<option value="ATTENDING">{m.guestList_accepted()}</option>
						{#if isMaybeAnswerEnabled($currentEvent)}
							<option value="MAYBE">{m.guestList_maybe()}</option>
						{/if}
						<option value="NOT_ATTENDING">{m.guestList_declined()}</option>
					</select>
					<LoadingButton
						disabled={!newGuestName || !newGuestAttending}
						onClick={async () => await addNewGuest()}
						class="btn btn-primary ml-2 pl-2 pr-0 text-white sm:ml-4 sm:pl-4"
					>
						<IconPlusCircle />
					</LoadingButton>
				</div>
				{#each guests as guest (guest.userId)}
					<div
						animate:flip={{ duration: 500, easing: cubicInOut }}
						in:fade={{ duration: 500 }}
						out:fade={{ duration: 150 }}
						class="mb-4 flex"
					>
						<InputText
							bind:value={guest.name}
							onkeyup={() => guestNameChanged(guest)}
							name="name"
							placeholder="Karla Kolumna"
							autocomplete="off"
							class="mr-2 w-full sm:mr-4"
						/>
						<select bind:value={guest.attending} onchange={() => onAttendingChanged(guest)} class={inputColorClasses}>
							<option value="ATTENDING">{m.guestList_accepted()}</option>
							<option disabled={!isMaybeAnswerEnabled($currentEvent)} value="MAYBE">{m.guestList_maybe()}</option>
							<option value="NOT_ATTENDING">{m.guestList_declined()}</option>
						</select>
						<button onclick={() => onRemoveGuest(guest)} class="pl-2 pr-0 sm:pl-4" title={m.delete_()}>
							<IconTrash />
						</button>
					</div>
				{/each}

				{#if $currentEvent.maxGuests}
					<h3
						bind:this={waitlistTitleElement}
						use:viewport
						onenterViewport={() => (waitlistInView = true)}
						onexitViewport={() => (waitlistInView = false)}
						class="label mb-2 mt-6"
					>
						{m.guestList_waitlistCount({ count: waitlist.length })}
					</h3>
					{#each waitlist as guest (guest.userId)}
						<div
							animate:flip={{ duration: 500, easing: cubicInOut }}
							in:fade={{ duration: 500 }}
							out:fade={{ duration: 150 }}
							class="mb-4 flex"
						>
							<InputText
								bind:value={guest.name}
								onkeyup={() => guestNameChanged(guest)}
								name="name"
								readonly
								disabled
								class="mr-2 w-full sm:mr-4"
							/>

							<LoadingButton onClick={async () => onMoveUpWaitlistEntry(guest)} class="btn btn-secondary w-fit">
								{m.guestList_addToEvent()}
							</LoadingButton>

							<button onclick={() => onRemoveGuest(guest)} class="pl-2 pr-0 sm:pl-4">
								<IconTrash />
							</button>
						</div>
					{/each}
				{/if}
			</ul>
		</div>
		<!-- 
	<div class="mb-36" />

	<pre class="hidden text-sm">
Hier kannst du hinterlegen wen du planst einzuladen oder schon eingeladen hast. 
Wenn die Personen zu oder abgesagt
haben kannst du sie zB löschen. VERBINDEN?

Außerdem kann man Emails für seine Gäste angeben und alle auf einen Rutsch einladen?
    oder noch besser telefonnummern und dann per sms einladen? MONETARISIERUNG!
        contacts können auf android chrome aus contacts picker ausgewählt werden 

Außerdem kann man Zu/Absagen bearbeiten. 
    auch Namen ändern?
    auch Begleitpersonen ändern?
    auch Items ändern?
	kommentar nicht ändern! ist übergriffig, man kann leuten dinge in den mund legen
	</pre>

        -->
	</Modal>
{/if}
